// ─────────────────────────────────────────────────────────────────────────────
// THE SKILL REGISTRY — every feature that calls a model, its active version,
// and per version the prompt and THE MODEL.
//
// ⭐ A SKILL'S MODEL LIVES HERE, IN ITS VERSION, AND NOWHERE ELSE. Every call
// site reads its model from this file (`skillModel`) and passes it on the
// request; the OpenRouter client has no default of its own. Changing a model is
// a new version — a PR and a release — exactly like changing a prompt. So a
// receipt's `creator_llm_run.prompt_version` names its model too, and history
// by version is history by model.
//
// ⚠️ NO ENV VAR PICKS A MODEL OR A VERSION. OPENROUTER_CREATOR_MODEL,
// WEEKLY_REPORT_MODEL, DECK_DRAFT_MODEL, AFFILIATE_DM_REPLIES_SKILL and
// WEEKLY_REPORT_DRAFT_SKILL are gone (removed 2026-10-14);
// tests/unit/skill-registry.test.ts fails on any read of them. (GRADING_RUBRIC
// still picks the rubric: grading's versions below are its rubrics, and the
// grader reads the model of the one it runs.)
//
// ⚠️ A VERSION IS NEVER EDITED, IT IS COPIED. A receipt says the version that
// made it, so editing a version's model would rewrite what every stored
// receipt of that version means. Add a version; move `active`.
//
// Each version key is the exact `prompt_version` the feature's receipts carry.
// Where the prompt is a file under skills/, `file` names it (repo-relative);
// where it is still a string in code, `file` is null and `promptIn` says where,
// and tests/unit/skill-registry.test.ts holds `active` equal to that code's
// version constant, so the two cannot drift.
//
// ⭐ SO DOES ITS DAILY COST LIMIT'S DEFAULT:
// `dailyLimitUsd`, per feature, beside its versions. It is the default of
// three layers, the last one set winning: this value, then env's SKILL_DAILY_LIMIT_USD_<FEATURE>
// (the key derived from the feature, lib/llm-budgets.ts `skillLimitKey`), then a
// value an admin sets on the skill's page (a feature flag under the same key).
// So a limit changes without a release; this default still takes a PR. A
// budget's kill switch (CREATOR_LLM_DAILY_COST_LIMIT_USD,
// CLIENT_SLACK_LLM_DAILY_COST_LIMIT_USD) at `0` beats all three. Only
// lib/llm-budgets.ts reads those env names (tests/unit/skill-registry.test.ts).
// The limit is the feature's, not a version's: a version is in every input
// hash, so a limit on a version would re-read everything to change it.
//
// ⭐ A DAILY LIMIT IS A RUNAWAY GUARD, NEVER A BUDGET: it stops a runaway
// process spending an unlimited amount, and high legitimate usage is simply
// legitimate cost. So a limit is sized well above any day legitimate use could
// reach, and $10 a day at the least: past it, something is stuck in a loop. It
// is never there to ration a busy day, and the limits are not split from a
// family total (the old "Shared $30, Client Slack $15" is gone); each stands
// alone, and the test holds every one at $10 or more. A skill that reaches its limit has
// likely gone wrong, so look at what it was doing before raising it.
//
// ⭐ SO DOES ITS NAME: one consistent display name per skill, `name`, per
// feature, set here and nowhere else. Every place that shows a skill reads it (`skillName`), and
// tests/unit/skill-names.test.ts fails on a nav item, a page title or a skill
// page that types a different one.
//
// ⭐ AND WHAT IT DOES: `summary`, one plain
// sentence saying what the skill does and where it is applied (the press or
// the job that runs it). It is the subtitle under every skill page's title and
// its row on the Skills page, and the hover on its LLM costs row; refusals
// and alerts keep the bare name. tests/unit/skill-names.test.ts holds every
// summary to its length cap, every name to what the nav fits, and refuses a
// summary typed anywhere else.
//
// ⭐ AND WHERE ITS PAGE IS: `slug`, so every skill page is `/skills/<slug>`
// (`skillPath`), whichever area runs it, and keeps that address however the
// areas are rearranged. tests/unit/skill-urls.test.ts holds each slug to kebab-case and
// unique, and every old address to a permanent redirect.
//
// Which budget a feature is grouped under, and the budget's label, are
// lib/llm-budgets.ts's: a budget is a group (a tab, an alert's name, a kill
// switch), not a sum. The model is this file's. The transport still refuses a
// model outside its feature's family (lib/server/openrouter-client.ts: a
// concrete Sonnet, or for the two drafts and the client Slack skills a
// concrete Claude); the test checks every version here against that rule.
//
// ZERO imports, fs-free, erasable TypeScript: the pages, the server, client
// components and the sidecars (bare node, strip-only) all read it.
// ─────────────────────────────────────────────────────────────────────────────

export type SkillFeature =
  | "fathom_meeting"
  | "brief_parser"
  | "candidate_review"
  | "first30_profile"
  | "dm_replies"
  | "creator_video"
  | "client_slack_triage"
  | "client_slack_action_items"
  | "client_slack_segment"
  | "client_slack_answered"
  | "client_slack_setaside"
  | "client_slack_respond"
  | "client_slack_item_kind"
  | "client_slack_items_check"
  | "client_slack_recheck"
  | "weekly_report_draft"
  | "deck_draft"
  | "ftc_request"
  | "grading"
  | "script_import"
  | "creator_ai_content"
  | "creator_persona_fit";

export interface SkillVersion {
  /** The OpenRouter model id every call on this version asks for. */
  model: string;
  /** The prompt file, repo-relative, or null when the prompt is in code (`promptIn`). */
  file: string | null;
}

export interface SkillEntry {
  /**
   * The skill's one display name: its nav item,
   * its page's title, its LLM costs row, the limit alert and the retired
   * skills list all read it from here. Words only: audit kinds, receipts'
   * `feature` and the page's `slug` keep their spelling and never follow a rename.
   */
  name: string;
  /**
   * The skill's page is `/skills/<slug>` (`skillPath`). Kebab-case, unique, and set from
   * the name when the page moved there. A URL people bookmark and paste into
   * Slack, so it does NOT follow a later rename of `name`: a new slug needs
   * a redirect from the old one (next.config.mjs, lib/skill-redirects.mjs).
   */
  slug: string;
  /**
   * What the skill does and where it is applied, in one plain sentence:
   * "Grades a script … when …". The subtitle under its page's title and
   * the hover on its nav item. Written from the code that calls it, so it
   * changes when the press or the job that runs it does.
   */
  summary: string;
  /** The version new calls use: a key of `versions`. */
  active: string;
  /**
   * Dollars per UTC day this feature's calls may spend, counted the ceiling's
   * way (lib/llm-budgets.ts: an unpriced success counts $0.10). `0` refuses
   * every call (a retired skill), and no override revives it. Every live skill
   * has one, $10 or more: the default, which env and the skill page override
   * (lib/llm-budgets.ts `limitOf`).
   */
  dailyLimitUsd: number;
  /** Where the prompt lives when it is not a file under skills/. */
  promptIn: string | null;
  versions: Readonly<Record<string, SkillVersion>>;
  /**
   * RETIRED: the UTC day (YYYY-MM-DD) its replacement shipped, absent while it
   * is live. A retired skill moves from the Skills page's Active
   * tab to its Retired tab (/skills?tab=retired); its pages, receipts and
   * entry stay. Its daily limit is $0, so any call is refused
   * (tests/unit/skill-registry.test.ts holds the two together).
   */
  retired?: string;
}

const SONNET_5 = "anthropic/claude-sonnet-5";
const OPUS_5 = "anthropic/claude-opus-5";
const OPUS_5_5 = "anthropic/claude-opus-5.5";
const SONNET_5_5 = "anthropic/claude-sonnet-5.5";
// The one non-Claude model: the AI content check watches video with its sound,
// which no Claude takes. Gemini 3.5 Flash-Lite, at low reasoning effort, with
// private (ZDR) endpoints: on the 2026-10-19 spot check it was the one cheap
// Gemini that called AI-generated try-on footage AI-made (2.5 Flash-Lite, 2.5
// Flash, 3.1 Flash-Lite and 3.8 Flash all called it real), and it called every
// real creator's video real.
const GEMINI_3_5_FLASH_LITE = "google/gemini-3.5-flash-lite";
const HAIKU_5_5 = "anthropic/claude-haiku-5.5";

/**
 * The first entries are what prod ran on 2026-10-14, version for version:
 * every creator-model feature on Sonnet 5 (prod set no
 * OPENROUTER_CREATOR_MODEL, so the code's default), DM Replies and grading on
 * the Sonnet 5 they always named, Deck draft on the Opus 5 default. The one
 * change made on purpose is the Weekly Report draft's v2.
 *
 * The daily limits (see the header): $10 for every
 * skill a background job or a press can run, DM Replies' $20 kept. Each is
 * several times any day its skill was measured to spend (E7–E10 and
 * prod's receipts: DM Replies' busiest day $7.66, action items' $4.37, brief
 * parsing's $0.13; respond hit its old $1.50 on the reseed's day, 2026-10-15,
 * the kind of stop these sizes rule out), so reaching one means a loop, not a
 * busy day. The retired triage stays at $0, refused. The On demand budget's three (each
 * call a person pressing) had no limit until they were given $100 each: high
 * enough that no real day reaches it, so it refuses a press past it as every
 * other limit does.
 */
export const SKILLS: Readonly<Record<SkillFeature, SkillEntry>> = {
  fathom_meeting: {
    name: "Meeting Review",
    slug: "meeting-review",
    summary: "Proposes a meeting's summary, decisions and commitments from its transcript when its owner presses Process transcript in Your Fathom.",
    active: "fathom-meeting-v1", dailyLimitUsd: 25, promptIn: null,
    versions: { "fathom-meeting-v1": { model: SONNET_5_5, file: "skills/meetings/fathom.v1.md" } },
  },
  brief_parser: {
    name: "Brief Parser",
    slug: "brief-parser",
    summary: "Reads a pasted casting request into a brief's fields, each backed by a quote, when someone presses Read it on Creator Selection.",
    dailyLimitUsd: 10,
    active: "brief-parser-v4",
    promptIn: "sourcing/prompts/brief-parser.md, with the reply's schema added in lib/server/brief-llm-parser.ts (BRIEF_PARSER_PROMPT_VERSION, lib/creator-llm-policy.ts)",
    versions: { "brief-parser-v4": { model: SONNET_5, file: null } },
  },
  candidate_review: {
    name: "Candidate Review",
    slug: "candidate-review",
    summary: "Assesses a casting's shortlist of up to 30 creators, the top 10 in full, each time the roster is ranked for it on Creator Selection.",
    dailyLimitUsd: 10,
    active: "creator-review-v3",
    promptIn: "sourcing/prompts/creator-review.md, read by lib/server/creator-review.ts (CREATOR_REVIEW_PROMPT_VERSION, lib/creator-llm-policy.ts)",
    versions: { "creator-review-v3": { model: SONNET_5, file: null } },
  },
  first30_profile: {
    name: "First 30 Profile",
    slug: "first-30-profile",
    summary: "Reads a client's documents into a profile, each fact quoted, on Start from documents in Client Onboarding or documents added to a record.",
    dailyLimitUsd: 10,
    active: "first30-profile-v3",
    promptIn: "sourcing/prompts/first30-profile.md, with the reply's shape added in lib/server/first30-intake.ts (FIRST30_PROFILE_PROMPT_VERSION, lib/first30-intake-policy.ts)",
    versions: {
      "first30-profile-v2": { model: SONNET_5, file: null },
      "first30-profile-v3": { model: SONNET_5, file: null },
    },
  },
  dm_replies: {
    name: "DM Replies",
    slug: "dm-replies",
    summary: "Drafts replies to creators' DMs for each affiliate brand every half hour and on Draft new replies; nothing is sent until a person does.",
    dailyLimitUsd: 20,
    // v3: the thread marks the shop's automated lines `us (automated)` and
    // names each card's kind. v4: a creator Reacher has at Content Unfulfilled
    // carries `video_owed`, and the draft asks for the video. Back to an earlier
    // version is this one line: the pass renders the request for the active
    // version, so it is sent exactly as before.
    active: "dm-replies-v4",
    promptIn: null,
    versions: {
      "dm-replies-v1": { model: SONNET_5, file: "skills/affiliate-dm-replies/dm-replies.v1.md" },
      "dm-replies-v2": { model: SONNET_5, file: "skills/affiliate-dm-replies/dm-replies.v2.md" },
      "dm-replies-v3": { model: SONNET_5, file: "skills/affiliate-dm-replies/dm-replies.v3.md" },
      "dm-replies-v4": { model: SONNET_5, file: "skills/affiliate-dm-replies/dm-replies.v4.md" },
    },
  },
  // The creator video check (face + talking on Sample requests): the cover and
  // 3 frames of one video per call, on the small model (~$0.002 a creator).
  // Run from Sample requests' Check face on camera button (21 Oct); the runaway guard's $10.
  creator_video: {
    name: "Face on Camera",
    slug: "face-on-camera",
    summary: "Checks a creator's video for a real face talking to camera when someone presses Check face on camera on a brand's Sample requests.",
    dailyLimitUsd: 10,
    active: "face-on-camera.v1",
    promptIn: null,
    versions: { "face-on-camera.v1": { model: HAIKU_5_5, file: "skills/face-on-camera/face-on-camera.v1.md" } },
  },
  // The two client Slack skills: the prompts moved out of
  // lib/client-slack-prompts.ts word for word, so their versions and every
  // input hash are what they were. The active version is in each wait's and
  // each conversation's input hash: moving it re-reads them all.
  client_slack_triage: {
    name: "Inbox Triage",
    slug: "inbox-triage",
    summary: "Read each client message waiting in the Client Inbox, every half hour, to say if and how to reply, until the Reply Advisor replaced it.",
    // RETIRED: the respond skill below replaced it. A registry
    // $0 is off (lib/llm-budgets.ts `limitOf`), so any call is refused; the entry
    // stays for its old receipts, which its page and LLM costs still read.
    // Its page is off the nav and on the retired skills list.
    dailyLimitUsd: 0,
    // v0.79.0, which shipped the respond skill (#591).
    retired: "2026-10-15",
    active: "triage.v1",
    promptIn: null,
    versions: { "triage.v1": { model: SONNET_5, file: "skills/client-slack-triage/triage.v1.md" } },
  },
  client_slack_action_items: {
    name: "Action Item Tracker",
    slug: "action-item-tracker",
    summary: "Reads each changed client Slack conversation every half hour and keeps the brand's list on Action Items: new asks, status and who owes.",
    // items.v3 measured ≈ $1.57 a day, ≈ $2.14 with the segmenter's moves
    // (E7); the limit is the runaway guard's $10.
    dailyLimitUsd: 10,
    // items.v3 (E6, E7, E11): the ledger. One call per segmenter
    // conversation, against the brand's list, answering new items and touches
    // with histories keyed to messages. The file is E11's measured
    // `items-list.v2` (eval/client-slack-prompts/) less its provenance line,
    // on the Sonnet 5.5 E7 re-picked from Opus through the API.
    // items.v4 (I1, S1): every item is YWS's, and
    // each history step says who has the ball (`owes`), which the store moves
    // onto the item. The file is I1's measured `items-v4` less its provenance
    // line; Sonnet 5.5, since Opus tied it at twice the price (I1, I2).
    // items.v5 (R3a): the first composed from the labelling
    // guide (docs/client-slack-rulebook.md; `items.v5.source.md` beside it),
    // with D20–D23 and complete membership; v4's answer, schema and list line
    // unchanged. Measured on all of gold v7 against v4 (R3a: 217 against 201
    // of 308 right, open-or-not 0.898 / owes 0.924 against 0.843 / 0.810 on
    // the 236 asks both match, no anchor lost), so active. Going active
    // re-reads the 30 days (the version is in each read's hash): ≈ $8 once.
    // items.v7 (the recall build, B): composed from guide v13, with the
    // new `standing` section (§2.4, F3's, amended) and D29 (a promise naming
    // a deliverable or a date is an ask), plus F3's standing examples; F2's
    // grain examples were measured and dropped (they closed split asks
    // early). v4's answer, schema and list line unchanged. There is no
    // items.v6: that number is F2's unshipped candidate
    // (eval/client-slack-prompts/items.v6.md). Measured in prod's 14-day
    // frame on all of gold v7 against items.v5's two reads
    // (the recall build's measurement): standing 4 → 13 of 17,
    // right 224 → 242, no anchor lost. Its early closures (6 against the
    // re-read's 2) failed the rule set before the read; it shipped on the
    // pieces (2026-10-24), the one real shape (work sent for review
    // closed at delivery) left for items.v8. Going active re-reads the 30
    // days, newest first, at most 80% of the limit a day (R3a2).
    // items.v8 (V8): guide v14's D30 (work delivered for the client's review
    // stays open, the client owing, until they approve or ask for changes;
    // our "done, can you check it?" on a fix is done) and the plan-question
    // line in `standing`, plus one worked example. Read twice in prod's frame
    // on all of gold v7 against items.v7's read
    // (the V8 review-open measurement): the four review
    // asks 0 → 4 of 4 in both reads, right 223 and 245 against 242, no
    // anchor lost. Going active re-reads the 30 days as v7 did (R3a2).
    // items.v9 (X4): guide v15's D31 (a ✅ as the client's whole answer is
    // their yes) and D32 (a client offering another way leaves the ball with
    // us until any reply) pasted into `status` and `owes`; items.v8's source
    // otherwise byte for byte, so no read of its own (the rulings were
    // measured where they act, in X4's re-check, and items.v8's text is
    // unchanged). Going active re-reads the 30 days as v8 did (R3a2).
    active: "items.v9",
    promptIn: null,
    // items.v1 was in code and has no file; its receipts keep their own model.
    versions: {
      "items.v2": { model: SONNET_5, file: "skills/client-slack-items/items.v2.md" },
      "items.v3": { model: SONNET_5_5, file: "skills/client-slack-items/items.v3.md" },
      "items.v4": { model: SONNET_5_5, file: "skills/client-slack-items/items.v4.md" },
      "items.v5": { model: SONNET_5_5, file: "skills/client-slack-items/items.v5.md" },
      "items.v7": { model: SONNET_5_5, file: "skills/client-slack-items/items.v7.md" },
      "items.v8": { model: SONNET_5_5, file: "skills/client-slack-items/items.v8.md" },
      "items.v9": { model: SONNET_5_5, file: "skills/client-slack-items/items.v9.md" },
    },
  },
  // The segmenter (E3, E8, E10): cuts each channel's last 72
  // hours into conversations, every tick that brings a new top-level message.
  // segment.v1 is E3's measured `segment-c.v1` (eval/client-slack-prompts/)
  // less its provenance line, on the Opus 5.5 E3, E7 and E10 picked over
  // Sonnet (0.89 against 0.86 segment F1). About 48 calls a day at about $0.03,
  // so ≈ $1.4 a day (half that through OpenRouter); its limit is the
  // runaway guard's $10.
  client_slack_segment: {
    name: "Conversation Segmenter",
    slug: "conversation-segmenter",
    summary: "Cuts each client Slack channel's last 72 hours into conversations every half hour, which the Client Inbox and Action Items then read.",
    dailyLimitUsd: 10,
    active: "segment.v1",
    promptIn: null,
    versions: { "segment.v1": { model: OPUS_5_5, file: "skills/client-slack-segment/segment.v1.md" } },
  },
  // The answered skill (E4, E7, E9, E10): one call per client
  // turn, reading the turn's whole conversation, says whether YWS owed a
  // response and what answered it. answered.v1 is E4's measured
  // `answered-c.v1` (eval/client-slack-prompts/) less its provenance line, on
  // the Sonnet 5.5 E4 picked and E7's OpenRouter spot test held (answered F1
  // 0.95). About $0.90 a day measured, $1.30 with the segmenter's moves
  // re-reading both conversations (E9); its limit is the runaway guard's $10.
  // answered.v2 (A1, D3–D5): a client finishing
  // our task or answering our question is not owed, a target with no question
  // is not owed, disappointment is, and a YWS 👍/✅ answers a request while 👀
  // answers nothing. Measured as the file stands (20 of 22 of Jonathan's
  // anchors against v1's 10; the untouched gold slice tied at 0.933). Moving
  // the version re-reads, once, every turn inside its 3 days and every open
  // read at any age: 391 calls on the 2026-10-18 snapshot (337 open,
  // 51 young, 3 never read), ≈ $4 at A1's ≈ $0.01 an open-window call,
  // inside the $10 day.
  // answered.v3 (D10): v2 plus a standing order
  // isn't owed (a target or direction that ends "keep us posted"). Measured
  // as the file stands: 21 of 22 anchors against v2's 20 (Dan's ROAS turn),
  // the untouched gold slice within noise (0.947 against 0.933), the
  // snapshot's real asks that say "keep me posted" still owed. Moving the
  // version re-reads once, as v2's did: every turn inside its 3 days and
  // every open read, at most the 411 counted on the 2026-10-18 snapshot
  // (fewer, now that v2 has closed some), ≈ $4, inside the $10 day.
  // answered.v4 (R3b) is composed from guide v11 and was never
  // active: it lost anchor #17 in its read and fell 6 short on gold v6's
  // untouched slice, mostly reviews with changes D27 now calls owed.
  // answered.v5 (R3d) is composed from guide v12 (D27: a review of
  // our work that asks for changes is owed a full reply). Measured on gold v8
  // against today's v3 + setaside.v2: anchors 22 of 22, touched slice 60
  // against 54 of 75, untouched 68 against 69 (−0.013, −0.067 to +0.040),
  // gold v7's waits 272 against 259 of 293. So active, alone: the second look
  // nets −4 on top of it (setaside below). Going active re-reads ≈ 217 turns
  // once, ≈ $1.8, newest first under R3a2's 80% reserve.
  client_slack_answered: {
    name: "Answer Finder",
    slug: "answer-finder",
    summary: "Decides every half hour whether a client's Slack message was owed a reply and what answered it, for the Client Inbox and Response Times.",
    dailyLimitUsd: 10,
    active: "answered.v5",
    promptIn: null,
    versions: {
      "answered.v1": { model: SONNET_5_5, file: "skills/client-slack-answered/answered.v1.md" },
      "answered.v2": { model: SONNET_5_5, file: "skills/client-slack-answered/answered.v2.md" },
      "answered.v3": { model: SONNET_5_5, file: "skills/client-slack-answered/answered.v3.md" },
      "answered.v4": { model: SONNET_5_5, file: "skills/client-slack-answered/answered.v4.md" },
      "answered.v5": { model: SONNET_5_5, file: "skills/client-slack-answered/answered.v5.md" },
    },
  },
  // The set-aside check (A2): one small
  // call per client turn the answered skill set aside as finished (a
  // delivery, an approval, "done": lib/client-slack-setaside.ts
  // `setasideCandidate`), asking only whether it also hands YWS something
  // new. A yes puts the wait back as owed. setaside.v1 is A2's measured
  // prompt on Sonnet 5.5 (Haiku 5.5 over-called it: both calls −0.08 on the
  // touched slice). A turn is asked once, and again only if it or what came
  // before it changes. Its first tick checks every candidate in the 90 days:
  // 381 on the 2026-10-18 snapshot's kept reads (more once v2 and v3 have set
  // more aside, ≈ 400–550), ≈ $0.0063 a call on A2's runs, so ≈ $3 once;
  // then a few a day. The runaway guard's $10.
  // setaside.v2 (D17, A2) asks the ball
  // question instead: was the turn YWS's ask coming back (not owed, whatever
  // caveat it carries), or unprompted information YWS should at least
  // acknowledge? On Jonathan's 25 boundary turns of 2026-10-19 it put back
  // none he called not owed (v1: 7 of 13), kept both he called owed, and the
  // 31 anchors held. Same call, same answer, same Sonnet 5.5; v1 is kept for
  // rollback. The version is in the kept hash, so the first tick on v2 asks
  // every candidate again once: 463 on the 2026-10-18 22:44 snapshot, ≈ $1.35
  // at prod's measured ≈ $0.0029 a call.
  // B1 widens its selector, not its prompt (`ackHintTurn`: a turn
  // the old words call acknowledgements only that isn't plainly one). Same
  // version, so nothing kept is asked again; the first tick asks the new
  // turns once: 125 on the 2026-10-20 02:08 snapshot, ≈ $0.38.
  // RETIRED (R3d): once answered composes `owed` whole (answered.v5,
  // guide v12), the second look doesn't earn its call. setaside.v4 on top of
  // answered.v5 put back 12 turns on gold v8, 4 owed and 8 not (−4 net; R3b's
  // setaside.v3 on answered.v4: −3). Its kept put-backs keep counting for
  // the turns they were made on; the tick no longer runs it
  // (tools/client-slack-sync.ts), and a registry $0 refuses any call.
  client_slack_setaside: {
    name: "Second Look",
    slug: "second-look",
    summary: "Took a second look, every half hour, at client messages the Answer Finder set aside as finished, putting new asks back in the Client Inbox.",
    dailyLimitUsd: 0,
    retired: "2026-10-23",
    active: "setaside.v2",
    promptIn: null,
    versions: {
      "setaside.v1": { model: SONNET_5_5, file: "skills/client-slack-setaside/setaside.v1.md" },
      "setaside.v2": { model: SONNET_5_5, file: "skills/client-slack-setaside/setaside.v2.md" },
      "setaside.v3": { model: SONNET_5_5, file: "skills/client-slack-setaside/setaside.v3.md" },
      "setaside.v4": { model: SONNET_5_5, file: "skills/client-slack-setaside/setaside.v4.md" },
    },
  },
  // The respond skill (E5, E7, E10): one call per owed client
  // turn, at the first read where the answered skill says it is owed, told
  // where its clock starts and never what answered it: the inbox's kind (and
  // so the reaction gate), need line, escalation, emoji and quick replies.
  // respond.v1 is E5's measured `respond-b.v1` (eval/client-slack-prompts/)
  // less its provenance line, on Opus 5.5 (E10's repeated API runs: kind
  // +0.057 over Sonnet on the 50 hardest waits). About $0.53 a day measured;
  // its limit is the runaway guard's $10 (the old $1.5 stopped it on the
  // reseed's day). respond.v2 (R3c) is composed from guide v11 and
  // inactive: it fixes D18's future step (9 of 9 reads against 7) and ties
  // kind, but loses 4 escalations of 315 (303 against 307), so the gate
  // failed. respond.v3 (R3d) is composed from guide v12 (D27's
  // review is `reply`; D28's disputed or unexpected
  // charge, upset over our advice, complaint about a YWS person escalate).
  // On gold v8's 317 owed waits against v1: kind 306 against 298, escalation
  // 309 against 307, the future step 9 of 9 reads against 7, 11 of 11
  // anchors against 9. So active. Going active re-reads ≈ 211 open owed
  // turns once, ≈ $3.0, newest first under R3a2's 80% reserve.
  client_slack_respond: {
    name: "Reply Advisor",
    slug: "reply-advisor",
    summary: "Says how to answer each owed client message in the Client Inbox, every half hour: a quick or a written reply, what's needed, and escalation.",
    dailyLimitUsd: 10,
    active: "respond.v3",
    promptIn: null,
    versions: {
      "respond.v1": { model: OPUS_5_5, file: "skills/client-slack-respond/respond.v1.md" },
      "respond.v2": { model: OPUS_5_5, file: "skills/client-slack-respond/respond.v2.md" },
      "respond.v3": { model: OPUS_5_5, file: "skills/client-slack-respond/respond.v3.md" },
    },
  },
  // The Work Type skill (B2b): one small call per task, on its
  // title and description alone, naming which of YWS's kinds of work it is
  // (N14's catalog) for the Action Items page's Work type column. It never
  // touches status or who owes. Asked once, and again only when the title or
  // description changes; a person's choice is never asked over. kind.v1 on
  // Haiku 5.5: on 60 prod titles held out from the prompt's writing, 53 and
  // 52 of 60 over two runs as the client sends it, against one labeller's
  // kinds (Sonnet 5.5 51, at about 19 times the price; N14's word rule 32),
  // ≈ $0.0002 a call. The first tick reads every task once: 645 on the
  // 2026-10-20 02:08 snapshot, ≈ $0.13. The runaway guard's $10.
  client_slack_item_kind: {
    name: "Work Type",
    slug: "work-type",
    summary: "Names each action item's type of work from its title and description, every half hour, for the Work type column on Action Items.",
    dailyLimitUsd: 10,
    active: "kind.v1",
    promptIn: null,
    versions: { "kind.v1": { model: HAIKU_5_5, file: "skills/client-slack-item-kind/kind.v1.md" } },
  },
  // The Missed Items check (C2; F4c's check-v2): after
  // the items read of a conversation that holds a person's message of 400+
  // characters from the last 14 days, one call reads the long messages point
  // by point against the brand's list and only ADDS the requests and
  // promises the list lacks, each marked as the check's. It never changes,
  // closes or re-balls a listed item. check.v2 is F4c's measured check-v2
  // (precision 0.88, nothing broken, 2 of F4's 9 stable plain misses, ≈ 0.3
  // adds a brand-week), recomposed from guide v14 (D29's promise line, D30,
  // and §2.4 `standing`, which `status` now points to); re-measured on items.v8's read
  // (the C2 live check's measurement): the 9 stable
  // misses 2 of 9 again, nothing broken, but precision 0.71 on 17 adds, so
  // borderline: the pass runs only while ACTION_ITEMS_MISSED_ITEMS_CHECK is on
  // (/settings/flags, default off). About $0.015 a gated conversation; its first
  // tick on checks the 14 days' gated conversations once (≈ 250, ≈ $4). The
  // runaway guard's $10.
  client_slack_items_check: {
    name: "Missed Items",
    slug: "missed-items",
    summary: "Adds asks the Action Item Tracker missed in long client Slack messages to Action Items, every half hour, while its feature flag is on.",
    dailyLimitUsd: 10,
    // check.v3 (X4): check.v2's source composed from guide v15 (D31, D32 in
    // `status` and `owes`); nothing else changed, and the check is off.
    active: "check.v3",
    promptIn: null,
    versions: {
      "check.v2": { model: SONNET_5_5, file: "skills/client-slack-check/check.v2.md" },
      "check.v3": { model: SONNET_5_5, file: "skills/client-slack-check/check.v3.md" },
    },
  },
  // The re-check of quiet items (X4): at most once a week per brand (X4c), open
  // tasks and scheduling items quiet ≥ 5 working days are read again with the
  // brand's list and channels, and each is ended (a suggestion to confirm),
  // inactive (marked so past 10 working days and 5 untouched conversations),
  // live, or merged into the earlier item it restates. recheck-v2 on Opus 5.5
  // at high effort, as measured on X3's 328 prod items
  // (the X4 re-check measurement): 0.848 five-way and
  // 0.921 ended-or-not against the judges' 0.866 / 0.915; X4c's narrow window
  // (from the re-checked items' last messages) 0.832 / 0.912, run-to-run
  // variation, at ≈ 32k input tokens a call against 40k. ≈ $0.20 a call, at
  // most one read a brand-week (X4c: ≈ 0.73 calls a brand-week). The runaway
  // guard's $10.
  client_slack_recheck: {
    name: "Quiet Items Re-check",
    slug: "quiet-items-re-check",
    summary: "Re-reads a brand's action items quiet for 5 working days, at most weekly, and suggests Done, Dropped, inactive or a merge on Action Items.",
    dailyLimitUsd: 10,
    active: "recheck.v2",
    promptIn: null,
    versions: { "recheck.v2": { model: OPUS_5_5, file: "skills/client-slack-recheck/recheck.v2.md" } },
  },
  weekly_report_draft: {
    name: "Report Writing",
    slug: "report-writing",
    summary: "Writes a weekly report's words from the week's figures when an AM presses Generate report or Draft now, for the AM to review and send.",
    dailyLimitUsd: 100,
    active: "weekly-report-draft-v2",
    promptIn: null,
    versions: {
      // What prod ran: its .env set WEEKLY_REPORT_MODEL to Sonnet 5 over the
      // code's Opus 5 default. Recorded as it was, for v1's receipts' sake.
      "weekly-report-draft-v1": { model: SONNET_5, file: "skills/weekly-report-draft/draft.v1.md" },
      // v1's words, on Opus 5.5 (listed on OpenRouter 2026-10-14). Only the model changed, so the file is v1's.
      "weekly-report-draft-v2": { model: OPUS_5_5, file: "skills/weekly-report-draft/draft.v1.md" },
    },
  },
  deck_draft: {
    name: "Deck Draft",
    slug: "deck-draft",
    summary: "Drafts a client's strategy deck on Draft the judgment cells (v1) or Generate v3 draft (v3), saved as a new version for a person to edit.",
    dailyLimitUsd: 100,
    active: "deck-draft-v1",
    promptIn: "lib/deck-draft.ts (DECK_DRAFT_PROMPT_VERSION)",
    versions: {
      "deck-draft-v1": { model: OPUS_5, file: null },
      "deck-draft-v3": { model: OPUS_5, file: "skills/strategy-deck-v3/SKILL.md" },
    },
  },
  ftc_request: {
    name: "Creator Request",
    slug: "ftc-request",
    summary: "Drafts a client's full-time creator request from their account record when its sources change, or on Regenerate.",
    dailyLimitUsd: 10,
    active: "ftc-request-v1",
    promptIn: "skills/ftc-request/SKILL.md, with the application contract in lib/ftc-request.ts (FTC_REQUEST_PROMPT_VERSION)",
    versions: {
      "ftc-request-v1": { model: OPUS_5_5, file: "skills/ftc-request/SKILL.md" },
    },
  },
  grading: {
    name: "Script Grading",
    slug: "script-grading",
    summary: "Grades a script against the rubric, scoring each category with suggestions, when someone presses Grade Script in the script editor.",
    dailyLimitUsd: 100,
    // The rubric GRADING_RUBRIC picks (lib/grading/rubric-source.ts, the
    // latest when unset) is the version; every rubric grades on Sonnet 5.
    active: "rubric-v6",
    promptIn: null,
    versions: {
      "rubric-v2": { model: SONNET_5, file: "skills/grading/rubric.v2.md" },
      "rubric-v3": { model: SONNET_5, file: "skills/grading/rubric.v3.md" },
      "rubric-v4": { model: SONNET_5, file: "skills/grading/rubric.v4.md" },
      "rubric-v5": { model: SONNET_5, file: "skills/grading/rubric.v5.md" },
      "rubric-v6": { model: SONNET_5, file: "skills/grading/rubric.v6.md" },
      "brand-requirements-v1": { model: SONNET_5, file: "skills/grading/brand-requirements.v1.md" },
    },
  },
  // Script Import: one call per paste, file or sheet, from the script editor's
  // import, for creators moving their Google Docs in. It answers the structure
  // only (which lines are each script's title, hook and so on, or which of a
  // sheet's columns holds which field), never text: the app copies the lines
  // (lib/script-import-ranges.ts) and the cells, so every word is the writer's.
  // On demand, each call a person pressing: the On demand $100. Sonnet 5.5,
  // measured on real docs: the cheapest that read their lines right (Opus also
  // did, at nearly twice the cost; Haiku ran out of room on a two-week doc).
  script_import: {
    name: "Script Import",
    slug: "script-import",
    summary: "Reads a pasted Google Doc's or a file's structure, which lines are each script's title, hook and so on, for the script editor's import.",
    dailyLimitUsd: 100,
    active: "script-import.v1",
    promptIn: null,
    versions: { "script-import.v1": { model: SONNET_5_5, file: "skills/script-import/import.v1.md" } },
  },
  // The AI content check:
  // one call per video, a creator's 3 most recent, each watched with its sound,
  // so a specialist can skip creators whose videos are AI-made. ~$0.001 a call
  // measured; each creator is asked once in 30 days.
  // The runaway guard's $10.
  creator_ai_content: {
    name: "AI Content",
    slug: "ai-content",
    summary: "Watches a creator's 3 latest videos for an AI-made face, voice or footage on Check AI content, on Sample requests or a new campaign.",
    dailyLimitUsd: 10,
    active: "ai-content.v1",
    promptIn: null,
    versions: { "ai-content.v1": { model: GEMINI_3_5_FLASH_LITE, file: "skills/creator-ai-content/ai-content.v1.md" } },
  },
  // The fit score (1 Nov): one call reads one creator's stored videos and
  // covers against the brand's affiliate persona, on the small model that
  // judged the persona test (~$0.001 a creator). Run from Sample requests'
  // Check fit score button; the runaway guard's $10, as the face check.
  creator_persona_fit: {
    name: "Fit Score",
    slug: "fit-score",
    summary: "Scores a creator's fit with the brand's affiliate persona from their videos when someone presses Check fit score on Sample requests.",
    dailyLimitUsd: 10,
    active: "persona-fit.v1",
    promptIn: null,
    versions: { "persona-fit.v1": { model: HAIKU_5_5, file: "skills/persona-fit/persona-fit.v1.md" } },
  },
};

export const SKILL_REGISTRY_PATH = "skills/skills.ts";

/** Is this skill retired (`retired` on its entry)? */
export const isRetiredSkill = (feature: SkillFeature): boolean => SKILLS[feature].retired !== undefined;

/** Every retired skill, in the registry's order. */
export const retiredSkills = (): SkillFeature[] => (Object.keys(SKILLS) as SkillFeature[]).filter(isRetiredSkill);

/** The skill's display name: "Reply Advisor". The one every page, row and alert shows. */
export const skillName = (feature: SkillFeature): string => SKILLS[feature].name;

/** The skill's page: "/skills/reply-advisor". Every link to a skill page reads it. */
export const skillPath = (feature: SkillFeature): string => `/skills/${SKILLS[feature].slug}`;

/** What the skill does and where, in one sentence: its page's subtitle and its nav item's hover. */
export const skillSummary = (feature: SkillFeature): string => SKILLS[feature].summary;

/** The active version of a feature: the `prompt_version` its next call will record. */
export const activeSkillVersion = (feature: SkillFeature): string => SKILLS[feature].active;

/**
 * The model a feature's calls ask for: the active version's, or a named
 * version's. A version the registry doesn't list THROWS: a call on a prompt
 * nobody gave a model is the silent guess this file exists to end.
 */
export function skillModel(feature: SkillFeature, version: string = SKILLS[feature].active): string {
  const v = Object.hasOwn(SKILLS[feature].versions, version) ? SKILLS[feature].versions[version] : undefined;
  if (!v) throw new Error(`${feature} has no version "${version}" in ${SKILL_REGISTRY_PATH}`);
  return v.model;
}

/**
 * The feature's default daily cost limit in dollars. The registry's value
 * only; lib/llm-budgets.ts `limitOf` applies the overrides and the kill switch.
 */
export const skillDailyLimitUsd = (feature: SkillFeature): number => SKILLS[feature].dailyLimitUsd;
